import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StorefrontHeroActions from './components/StorefrontHeroActions';
import { useProducts } from './useProducts';
import PageSeoHelmet from './PageSeoHelmet';
import { formatDisplayTitle } from './formatProductTitle';
import CatalogImage from './catalog/CatalogImage';
import { firstProductImage } from './catalog/catalogMedia';
import { useSiteSettings } from './SiteSettingsContext';

function Shop() {
  const { settings: site } = useSiteSettings();
  const store = site.store_copy || {};
  const navigate = useNavigate();
  const [heroSearch, setHeroSearch] = useState('');
  const { categories, products, loading } = useProducts();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const dynamicCards = useMemo(() => {
    const sorted = [...categories].sort(
      (a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)
    );
    return sorted.map((c) => {
      const list = products[c.slug] || [];
      const img = firstProductImage(list);
      const n = list.length;
      return {
        key: c.slug,
        name: formatDisplayTitle(c.name || c.slug),
        path: `/shop/${c.slug}`,
        image: img,
        description:
          n > 0
            ? `Browse ${n} handcrafted design${n === 1 ? '' : 's'}.`
            : 'Collection coming soon.',
        products: n > 0 ? `${n}+ designs` : '—',
      };
    });
  }, [categories, products]);

  const gridCards = dynamicCards;
  const shopFeatures = Array.isArray(store.shop_features) ? store.shop_features : [];

  const submitHeroSearch = (e) => {
    e.preventDefault();
    const q = heroSearch.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="shop-page">
      <PageSeoHelmet page="shop" />

      <div className="container store-page__container">
        <section className="shop-hero">
          <div className="shop-hero-panel text-center">
            <p className="shop-hero-eyebrow">{store.shop_eyebrow || 'Saloni Designs'}</p>
            <h1 className="shop-page-title">{store.shop_title || 'Handcrafted collections'}</h1>
            <p className="shop-page-subtitle handwritten">
              {store.shop_subtitle ||
                'Rakhi, Lumba, and more — each piece made with care. Open a category to explore styles and designs.'}
            </p>
            <StorefrontHeroActions placement="shop" giftFinderPlacement="shop" />
            <form
              className="shop-hero-search mt-4"
              onSubmit={submitHeroSearch}
              role="search"
              aria-label="Search catalog"
            >
              <div className="input-group input-group-lg shadow-sm">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search by product name or ID…"
                  aria-label="Search by product name or ID"
                  value={heroSearch}
                  onChange={(e) => setHeroSearch(e.target.value)}
                  autoComplete="off"
                />
                <button type="submit" className="btn btn-primary">
                  Search
                </button>
              </div>
            </form>
            {loading ? <p className="small text-muted mt-2 mb-0">Loading latest categories…</p> : null}
          </div>
        </section>

        <section className="shop-categories">
          <h2 className="shop-section-heading text-center">
            {store.shop_section_heading || 'Browse by category'}
          </h2>
          <p className="shop-section-lead text-center text-muted mb-4">
            {store.shop_section_lead ||
              'Tap a collection to see everything inside — including kids, personalized, and festive styles.'}
          </p>
          <div className="row g-4 justify-content-center">
            {gridCards.map((category) => (
              <div className="col-md-6 col-lg-4" key={category.key || category.name}>
                <div className="shop-category-card">
                  <div className="category-image-wrapper">
                    <CatalogImage
                      src={category.image}
                      className="category-image"
                      alt={category.name}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                    <div className="category-overlay">
                      <Link to={category.path} className="btn btn-secondary btn-lg">
                        Explore Collection
                      </Link>
                    </div>
                  </div>
                  <div className="category-content">
                    <h3 className="category-title">{category.name}</h3>
                    <p className="category-desc">{category.description}</p>
                    <div className="category-meta">
                      <span className="category-count">{category.products}</span>
                    </div>
                    <Link to={category.path} className="btn btn-primary category-cta">
                      Shop Now →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="shop-features">
          <div className="row g-4">
            {shopFeatures.map((feat, i) => (
              <div className="col-md-4" key={`shop-feat-${i}`}>
                <div className="feature-card">
                  <div className="feature-icon">{feat.icon || '\u2728'}</div>
                  <h4 className="feature-title">{feat.title}</h4>
                  <p className="feature-desc">{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Shop;
